import { normalizeBuyerTicketConfiguration } from '@charitystack/shared/group-tickets/buyer';

const GROUP_FIELDS = [
  'groupSizeMode',
  'pricingMode',
  'minimumSeats',
  'maximumSeats',
  'seatCapacity',
  'seatsSold',
];

const DUPLICATE_RESET_FIELDS = [
  'archived',
  'ticketSalesPaused',
  'ticketSalesPauseReason',
  'ticketSalesPausedAt',
  'ticketSalesPauseIncidentID',
];

const NUMERIC_PAYLOAD_FIELDS = new Set([
  'price',
  'sold',
  'quantity',
  'seats',
  'minimumSeats',
  'maximumSeats',
  'seatCapacity',
  'seatsSold',
]);

const integerOr = (value, fallback = 0) => {
  if (value === '' || value === null || value === undefined) return fallback;
  const parsed = Number(value);
  return Number.isInteger(parsed) ? parsed : fallback;
};

const withoutFields = (ticket, fields) => {
  const result = { ...ticket };
  fields.forEach((field) => delete result[field]);
  return result;
};

export const changeEventTicketType = (ticket, type) => {
  if (ticket.type === type) return { ...ticket };

  if (type === 'SINGLE') {
    return withoutFields({ ...ticket, type }, [...GROUP_FIELDS, 'seats']);
  }

  const seats = Math.max(2, integerOr(ticket.seats, integerOr(ticket.minimumSeats, 2)));
  const quantity = Math.max(0, integerOr(ticket.quantity));
  const sold = Math.max(0, integerOr(ticket.sold));
  return withoutFields(
    {
      ...ticket,
      type: 'GROUP',
      groupSizeMode: 'FIXED',
      pricingMode: ticket.pricingMode === 'PER_SEAT' ? 'PER_SEAT' : 'FLAT',
      seats,
      seatCapacity: quantity * seats,
      seatsSold: Math.max(0, integerOr(ticket.seatsSold, sold * seats)),
    },
    ['minimumSeats', 'maximumSeats'],
  );
};

export const changeGroupSizeMode = (ticket, groupSizeMode) => {
  if (ticket.groupSizeMode === groupSizeMode) return { ...ticket };

  const quantity = Math.max(0, integerOr(ticket.quantity));
  const seatsSold = Math.max(0, integerOr(ticket.seatsSold));

  if (groupSizeMode === 'MINIMUM') {
    const minimumSeats = Math.max(2, integerOr(ticket.minimumSeats, integerOr(ticket.seats, 2)));
    const seatCapacity = Math.max(
      minimumSeats,
      seatsSold,
      integerOr(ticket.seatCapacity, quantity * minimumSeats),
    );
    return withoutFields(
      {
        ...ticket,
        type: 'GROUP',
        groupSizeMode,
        pricingMode: ticket.pricingMode === 'PER_SEAT' ? 'PER_SEAT' : 'FLAT',
        minimumSeats,
        maximumSeats: null,
        seatCapacity,
        seatsSold,
      },
      ['seats'],
    );
  }

  const seats = Math.max(2, integerOr(ticket.seats, integerOr(ticket.minimumSeats, 2)));
  return withoutFields(
    {
      ...ticket,
      type: 'GROUP',
      groupSizeMode: 'FIXED',
      pricingMode: ticket.pricingMode === 'PER_SEAT' ? 'PER_SEAT' : 'FLAT',
      seats,
      seatCapacity: quantity * seats,
      seatsSold,
    },
    ['minimumSeats', 'maximumSeats'],
  );
};

// Per-keystroke inventory edits. A Fixed group's seatCapacity is derived
// (quantity * seats) and the shared contract rejects it when stale, so it is
// recomputed here whenever both inputs are usable integers.
export const changeEventTicketInventory = (ticket, field, value) => {
  const parsed = parseInt(value, 10);
  const next = {
    ...ticket,
    [field]: field === 'seats' ? (Number.isInteger(parsed) ? parsed : '') : parsed || 0,
  };
  if (next.type !== 'GROUP' || next.groupSizeMode === 'MINIMUM' || next.seats === '') return next;

  const seatCapacity = Number(next.quantity) * Number(next.seats);
  if (Number.isSafeInteger(seatCapacity)) next.seatCapacity = seatCapacity;
  return next;
};

export const normalizeEventTicketForSubmit = (ticket, { resetInventory = false } = {}) => {
  const type = ticket.type === 'GROUP' ? 'GROUP' : 'SINGLE';
  const quantity = Math.max(0, integerOr(ticket.quantity));
  const sold = resetInventory ? 0 : Math.max(0, integerOr(ticket.sold));
  const normalized = withoutFields(
    {
      ...ticket,
      type,
      quantity,
      sold,
      ...(resetInventory ? { attendees: [] } : {}),
    },
    resetInventory ? DUPLICATE_RESET_FIELDS : [],
  );

  if (type === 'SINGLE') {
    return withoutFields(normalized, [...GROUP_FIELDS, 'seats']);
  }

  const pricingMode = ticket.pricingMode === 'PER_SEAT' ? 'PER_SEAT' : 'FLAT';
  const groupSizeMode = ticket.groupSizeMode === 'MINIMUM' ? 'MINIMUM' : 'FIXED';

  if (groupSizeMode === 'FIXED') {
    const seats = Math.max(2, integerOr(ticket.seats, 2));
    const seatCapacity = quantity * seats;
    if (!Number.isSafeInteger(seatCapacity)) {
      throw new RangeError('Fixed Group Ticket seat inventory exceeds the safe integer range');
    }
    return withoutFields(
      {
        ...normalized,
        groupSizeMode,
        pricingMode,
        seats,
        seatCapacity,
        seatsSold: resetInventory ? 0 : Math.max(0, integerOr(ticket.seatsSold, sold * seats)),
      },
      ['minimumSeats', 'maximumSeats'],
    );
  }

  const minimumSeats = Math.max(2, integerOr(ticket.minimumSeats, 2));
  const maximumSeats =
    ticket.maximumSeats === '' || ticket.maximumSeats === null || ticket.maximumSeats === undefined
      ? null
      : integerOr(ticket.maximumSeats);
  return withoutFields(
    {
      ...normalized,
      groupSizeMode,
      pricingMode,
      minimumSeats,
      maximumSeats,
      seatCapacity: Math.max(0, integerOr(ticket.seatCapacity)),
      seatsSold: resetInventory ? 0 : Math.max(0, integerOr(ticket.seatsSold)),
    },
    ['seats'],
  );
};

export const normalizeEventTicketDefinitions = (tickets) =>
  tickets.map((ticket) => normalizeEventTicketForSubmit(ticket));

export const normalizeEventTicketContractMetadata = (
  source = {},
  fallback = {},
  { minimumRevision = 0 } = {},
) => ({
  ticketContractVersion: integerOr(
    source?.ticketContractVersion,
    integerOr(fallback?.ticketContractVersion, 2),
  ),
  ticketMutationRevision: Math.max(
    0,
    integerOr(minimumRevision),
    integerOr(source?.ticketMutationRevision, integerOr(fallback?.ticketMutationRevision, 0)),
  ),
});

export const reconcileSavedEventTicketDefinitions = (persistedTickets, normalizedTickets) =>
  normalizeEventTicketDefinitions(
    (Array.isArray(persistedTickets) ? persistedTickets : normalizedTickets).filter(
      (ticket) => ticket.archived !== true,
    ),
  );

export const reconcileSuccessfulEventTicketMutation = ({
  persistedForm,
  normalizedTickets,
  fallbackContract,
  expectedRevision,
}) => {
  const minimumRevision = Math.max(0, integerOr(expectedRevision));
  const persistedRevision = integerOr(persistedForm?.ticketMutationRevision, -1);
  const responseIsCurrent = persistedRevision >= minimumRevision;

  return {
    tickets: reconcileSavedEventTicketDefinitions(
      responseIsCurrent ? persistedForm?.tickets : undefined,
      normalizedTickets,
    ),
    contract: normalizeEventTicketContractMetadata(
      responseIsCurrent ? persistedForm : {},
      fallbackContract,
      { minimumRevision },
    ),
  };
};

export const prepareEventTicketDefinitionsForSubmit = (tickets) =>
  normalizeEventTicketDefinitions(tickets).map((normalized) => {
    return Object.fromEntries(
      Object.entries(normalized).map(([key, value]) => [
        key,
        NUMERIC_PAYLOAD_FIELDS.has(key) && value != null && typeof value !== 'string'
          ? value.toString()
          : value,
      ]),
    );
  });

export const duplicateEventTicketDefinitions = (tickets, createID) => {
  const ticketIdMap = {};
  const duplicatedTickets = (Array.isArray(tickets) ? tickets : [])
    .filter((ticket) => ticket.archived !== true)
    .map((ticket) => {
      const newID = createID();
      ticketIdMap[ticket.id] = newID;
      return normalizeEventTicketForSubmit({ ...ticket, id: newID }, { resetInventory: true });
    });

  const duplicatedPromos = [];
  (Array.isArray(tickets) ? tickets : [])
    .filter((ticket) => ticketIdMap[ticket.id])
    .forEach((ticket) => {
      (ticket.promos || []).forEach((promo) => {
        const remappedTicket = { id: ticketIdMap[ticket.id], name: ticket.name };
        const existingPromo = duplicatedPromos.find((item) => item.codeName === promo.codeName);
        if (existingPromo) {
          existingPromo.tickets.push(remappedTicket);
          existingPromo.tickets2.push(remappedTicket);
          return;
        }
        duplicatedPromos.push({
          codeId: createID(),
          codeName: promo.codeName,
          codeType: promo.codeType,
          codeAmount: Number(promo.codeAmount),
          codeQuantity: Number(promo.codeQuantity) || 1,
          tickets: [remappedTicket],
          tickets2: [remappedTicket],
        });
      });
    });

  return { duplicatedTickets, duplicatedPromos, ticketIdMap };
};

export const getEventTicketMinimumSubtotal = (ticket) => {
  const price = Number(ticket?.price);
  if (!Number.isFinite(price) || price < 0) return 0;
  if (ticket?.type !== 'GROUP' || ticket.pricingMode !== 'PER_SEAT') return price;
  const seats =
    ticket.groupSizeMode === 'MINIMUM' ? Number(ticket.minimumSeats) : Number(ticket.seats);
  if (!Number.isSafeInteger(seats) || seats < 2) return 0;
  return price * seats;
};

// The live preview renders tickets through the strict shared group-ticket
// contract, which throws during render; anything it rejects keeps the last
// valid definition instead of crashing the workspace.
const isPreviewableEventTicket = (ticket) => {
  if (getEventTicketConfigurationErrors(ticket).length > 0) return false;
  try {
    normalizeBuyerTicketConfiguration(ticket, 2);
    return true;
  } catch {
    return false;
  }
};

export const reconcileEventTicketPreviewDefinitions = (tickets = [], previousTickets = []) => {
  const previousByID = new Map(
    previousTickets.filter((ticket) => ticket?.id).map((ticket) => [ticket.id, ticket]),
  );

  return tickets.flatMap((ticket) => {
    if (isPreviewableEventTicket(ticket)) return [ticket];
    const previous = ticket?.id ? previousByID.get(ticket.id) : undefined;
    return previous ? [previous] : [];
  });
};

export const getEventTicketConfigurationErrors = (ticket) => {
  const errors = [];
  const quantity = Number(ticket.quantity);
  const sold = Number(ticket.sold || 0);
  if (
    ticket.ticketSalesPaused !== true &&
    Number.isSafeInteger(quantity) &&
    Number.isSafeInteger(sold) &&
    sold > quantity
  ) {
    errors.push({
      field: 'quantity',
      message:
        ticket.type === 'GROUP'
          ? 'Group inventory cannot be lower than the groups already sold'
          : 'Ticket inventory cannot be lower than the tickets already sold',
    });
  }
  if (ticket.type !== 'GROUP') return errors;
  const groupSizeMode = ticket.groupSizeMode ?? 'FIXED';
  const pricingMode = ticket.pricingMode ?? 'FLAT';

  if (!['FIXED', 'MINIMUM'].includes(groupSizeMode)) {
    errors.push({ field: 'groupSizeMode', message: 'Choose Fixed or Minimum group sizing' });
  }
  if (!['FLAT', 'PER_SEAT'].includes(pricingMode)) {
    errors.push({ field: 'pricingMode', message: 'Choose flat or per-seat pricing' });
  }

  if (groupSizeMode === 'FIXED') {
    if (!Number.isSafeInteger(Number(ticket.seats)) || Number(ticket.seats) < 2) {
      errors.push({ field: 'seats', message: 'Fixed group seats must be at least 2' });
    }
    if (
      ticket.ticketSalesPaused !== true &&
      Number.isSafeInteger(Number(ticket.seatsSold)) &&
      Number.isSafeInteger(Number(ticket.quantity)) &&
      Number.isSafeInteger(Number(ticket.seats)) &&
      Number(ticket.seatsSold) > Number(ticket.quantity) * Number(ticket.seats)
    ) {
      errors.push({
        field: 'quantity',
        message: 'Group inventory cannot be lower than the seats already sold',
      });
    }
    if (
      Number.isSafeInteger(Number(ticket.quantity)) &&
      Number.isSafeInteger(Number(ticket.seats)) &&
      !Number.isSafeInteger(Number(ticket.quantity) * Number(ticket.seats))
    ) {
      errors.push({
        field: 'quantity',
        message: 'Fixed group seat inventory exceeds the supported range',
      });
    }
    return errors;
  }

  const minimumSeats = Number(ticket.minimumSeats);
  const maximumSeats =
    ticket.maximumSeats === '' || ticket.maximumSeats === null || ticket.maximumSeats === undefined
      ? null
      : Number(ticket.maximumSeats);
  const seatCapacity = Number(ticket.seatCapacity);

  if (!Number.isSafeInteger(minimumSeats) || minimumSeats < 2) {
    errors.push({ field: 'minimumSeats', message: 'Minimum seats must be at least 2' });
  }
  if (!Number.isSafeInteger(seatCapacity) || seatCapacity < minimumSeats) {
    errors.push({
      field: 'seatCapacity',
      message: 'Seat inventory must be at least the minimum seats',
    });
  }
  if (
    ticket.ticketSalesPaused !== true &&
    Number.isSafeInteger(Number(ticket.seatsSold)) &&
    seatCapacity < Number(ticket.seatsSold)
  ) {
    errors.push({
      field: 'seatCapacity',
      message: 'Seat inventory cannot be lower than the seats already sold',
    });
  }
  if (
    maximumSeats !== null &&
    (!Number.isSafeInteger(maximumSeats) ||
      maximumSeats < minimumSeats ||
      maximumSeats > seatCapacity)
  ) {
    errors.push({
      field: 'maximumSeats',
      message: 'Maximum seats must be between the minimum and seat inventory',
    });
  }
  return errors;
};
